import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Container, Tabs, Tab, CircularProgress, Button } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CategoryIcon from '@mui/icons-material/Category';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { useImage } from '../hooks/useImage';
import useProject from '../hooks/useProject';
import ProjectImageGallery from '../components/common/ProjectImageGallery';
import ImageGeneratorDialog from '../components/layout/ImageGeneratorDialog';
import ReferenceImageGeneratorDialog from '../components/layout/ReferenceImageGeneratorDialog';

/**
 * MyImages Page Component
 * Displays all images from user's projects in a gallery view
 */
const MyImages = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showImageGeneratorDialog, setShowImageGeneratorDialog] = useState(false);
  const [showReferenceDialog, setShowReferenceDialog] = useState(false);
  const [defaultProjectId, setDefaultProjectId] = useState(null);
  const [allImages, setAllImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  
  const { projects } = useProject();
  const { 
    loading, 
    projectImages, 
    imageCategories, 
    getProjectImages,
    getImageCategories
  } = useImage();

  // Get a default project for generating new images
  useEffect(() => {
    if (projects && projects.length > 0 && !defaultProjectId) {
      setDefaultProjectId(projects[0]._id);
    }
  }, [projects, defaultProjectId]);

  // Load images from all projects when component mounts
  useEffect(() => {
    const loadAllImages = async () => {
      if (!projects || projects.length === 0) return;
      
      setIsLoading(true);
      
      try {
        // Get images from all projects and combine them
        const allImagesArray = [];
        
        for (const project of projects) {
          const images = await getProjectImages(project._id, {
            category: selectedCategory !== 'all' ? selectedCategory : undefined,
            favorites: selectedTab === 1
          });
          
          // Add images to the combined array
          if (images && images.length > 0) {
            allImagesArray.push(...images);
          }
        }
        
        // Sort all images by date (newest first)
        allImagesArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        // Store the combined images
        setAllImages(allImagesArray);
        
        // Get categories from the first project (just for UI display)
        if (projects.length > 0) {
          await getImageCategories(projects[0]._id);
        }
      } catch (error) {
        console.error('Error loading images:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadAllImages();
  }, [projects, selectedTab, selectedCategory, getProjectImages, getImageCategories]);

  // Handler for tab changes (All/Favorites)
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Handler for category changes
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  // Dialog handlers
  const handleOpenImageGenerator = () => {
    setShowImageGeneratorDialog(true);
  };

  const handleCloseImageGenerator = () => {
    setShowImageGeneratorDialog(false);
  };
  
  // Reference image dialog handlers
  const handleOpenReferenceDialog = () => {
    if (selectedImages.length === 0) {
      alert("Please select at least one image as reference");
      return;
    }
    
    if (selectedImages.length > 4) {
      alert("You can only select up to 4 images as references");
      return;
    }
    
    setShowReferenceDialog(true);
  };
  
  const handleCloseReferenceDialog = () => {
    setShowReferenceDialog(false);
  };
  
  // Selection mode handlers
  const toggleSelectionMode = () => {
    setSelectionMode(prev => !prev);
    if (selectionMode) {
      setSelectedImages([]);
    }
  };
  
  // Handle selected images from the gallery
  const handleSelectedImagesChange = (images) => {
    setSelectedImages(images);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 500 }}>
          My Images
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Selection mode toggle button */}
          <Button
            variant={selectionMode ? "contained" : "outlined"}
            color={selectionMode ? "secondary" : "primary"}
            onClick={toggleSelectionMode}
            sx={{ textTransform: 'none' }}
          >
            {selectionMode ? "Cancel Selection" : "Select Images"}
          </Button>
          
          {/* Create from references button - only show when in selection mode and images are selected */}
          {selectionMode && selectedImages.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenReferenceDialog}
              sx={{ textTransform: 'none' }}
            >
              Create from {selectedImages.length} Reference{selectedImages.length !== 1 ? 's' : ''}
            </Button>
          )}
          
          {/* Generate new image button */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddPhotoAlternateIcon />}
            onClick={handleOpenImageGenerator}
            sx={{ textTransform: 'none' }}
          >
            Generate new image
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 3, p: 0, overflow: 'hidden' }} elevation={1}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            aria-label="image tabs"
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab 
              icon={<ImageIcon sx={{ mr: 1 }} fontSize="small" />} 
              iconPosition="start" 
              label="All Images" 
              id="tab-0" 
            />
            <Tab 
              icon={<FavoriteIcon sx={{ mr: 1 }} fontSize="small" />} 
              iconPosition="start" 
              label="Favorites" 
              id="tab-1" 
            />
          </Tabs>
        </Box>
      </Paper>

      {/* Categories */}
      {imageCategories && imageCategories.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CategoryIcon sx={{ mr: 1 }} fontSize="small" />
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Categories:
            </Typography>
          </Box>
          <Grid container spacing={1}>
            {imageCategories.map((category) => (
              <Grid item key={category.category}>
                <Button
                  variant={selectedCategory === category.category ? "contained" : "outlined"}
                  color="primary"
                  size="small"
                  onClick={() => handleCategoryChange(category.category)}
                  sx={{ textTransform: 'none' }}
                >
                  {category.category.charAt(0).toUpperCase() + category.category.slice(1)} ({category.count})
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Image Gallery */}
      {isLoading || loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : allImages.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          {/* Pass the allImages directly to avoid loading from server again */}
          <ProjectImageGallery
            projectId={defaultProjectId} // Just to satisfy the component props
            loadOnMount={false}
            allowDelete={true}
            selectable={false}
            multiSelectable={selectionMode}
            onMultiSelect={handleSelectedImagesChange}
            images={allImages} // Pass the custom images array
          />
        </Box>
      ) : (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8, 
            px: 2, 
            bgcolor: 'background.paper', 
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'divider'
          }}
        >
          <ImageIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No images found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {selectedTab === 0 
              ? 'You haven\'t generated any images yet.'
              : 'You haven\'t favorited any images yet.'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddPhotoAlternateIcon />}
            onClick={handleOpenImageGenerator}
            sx={{ textTransform: 'none' }}
          >
            Generate new image
          </Button>
        </Box>
      )}

      {/* Image Generator Dialog */}
      <ImageGeneratorDialog
        open={showImageGeneratorDialog}
        onClose={handleCloseImageGenerator}
        projectId={defaultProjectId}
      />
      
      {/* Reference Image Generator Dialog */}
      <ReferenceImageGeneratorDialog
        open={showReferenceDialog}
        onClose={handleCloseReferenceDialog}
        referenceImages={selectedImages}
        projectId={defaultProjectId}
      />
    </Container>
  );
};

export default MyImages;